import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import UnmatchedInvoicesModal from '../pages/InvoicesPage/UnmatchedInvoicesModal';
import InvoiceMissingModal from '../pages/TransactionsPage/InvoiceMissingModal';
import routes from '../../routes/routes.json';
import {EXPENSES_PAGE_SEARCH_KEYS} from '../../pages/ExpensesPage/expensesPage.constants';
import {DEFAULT_MODAL_PROPS, localStorageKeysConstants, popupSettingsConstants, systemHashKeysConstants} from '../../constants';
import {mainActions} from '../../state/actions/main.actions';
import {userActions} from '../../state/actions/user.actions';
import {locationHelpers, transactionsHelpers} from '../../utils/helpers';
import {
  getInvoiceMissingExpensesUrl,
  getInvoiceMissingTarget,
  INVOICE_MISSING_TARGETS,
} from '../../utils/helpers/invoiceMissing.helpers';
import {
  useBankingCompanyState,
  useEmployeeEmail,
  useEmployeeId,
  useHasObAccounts,
  useIsAdmin,
  useIsAuthenticated,
  useIsExpensesDisabled,
  useIsFeaturesLoaded,
  useIsPsd2ObAvailable,
  useIsTreezorEnabled,
} from '../../hooks';

const {OPEN_BANK_ACCOUNT_DETAILS, TRANSACTIONS_INVOICE_MISSING, UNMATCHED_INVOICES} = systemHashKeysConstants;

const HashWindowManager = ({
  children,
}) => {
  const navigate = useNavigate();
  const [unmatchedInvoicesModalProps, setUnmatchedInvoicesModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [transactionsInvoiceMissingModalProps, setTransactionsInvoiceMissingModalProps] = useState(DEFAULT_MODAL_PROPS);

  const dispatch = useDispatch();

  const { hash, search } = useLocation();

  const companyState = useBankingCompanyState();
  const employeeEmail = useEmployeeEmail();
  const employeeId = useEmployeeId();
  const hasObAccounts = useHasObAccounts();
  const isAdmin = useIsAdmin();
  const isAuthenticated = useIsAuthenticated();
  const isExpensesDisabled = useIsExpensesDisabled();
  const isFeaturesLoaded = useIsFeaturesLoaded();
  const isPsd2ObAvailable = useIsPsd2ObAvailable();
  const isTreezorEnabled = useIsTreezorEnabled();
  const popupSettings = useSelector((state) => state.user?.popupSettings ?? {});

  const handleOpenModal = (setter, path) => {
    if (!isAuthenticated) return;

    setter?.({ ...DEFAULT_MODAL_PROPS, open: true });
    path && navigate(path);
  }

  useEffect(() => {
    const { company_id: companyId } = locationHelpers.getSearchParamsFromLocation({ search });
    if (companyId) {
      localStorage.setItem(localStorageKeysConstants.COMPANY_ID, companyId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hash) {
      const hashCallbacks = {
        [OPEN_BANK_ACCOUNT_DETAILS]: handleOpenBankAccountDetails,
        [TRANSACTIONS_INVOICE_MISSING]: handleTransactionsInvoiceMissing,
        [UNMATCHED_INVOICES]: handleUnmatchedInvoices,
      }
      if (hashCallbacks.hasOwnProperty(hash)) {
        hashCallbacks[hash](hash);
      }
    }
  }, [hash, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!popupSettings) return; // Exit if popupSettings is null/undefined

    for (const key in popupSettings) {
      if (popupSettings[key]) {
        const hashKey = Object.entries(popupSettingsConstants).find(([_, value]) => value === key)?.[0];
        const hashValue = systemHashKeysConstants[hashKey];

        if (hashValue) {
          navigate(`/${hashValue}`);
          return; // Exit early after first valid navigation
        }
      }
    }
  }, [popupSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelInvoiceMissingModal = () => setTransactionsInvoiceMissingModalProps(DEFAULT_MODAL_PROPS);

  // Decided here, at click time, so an unloaded company flag is never read as "expenses enabled".
  // `isAccountAvailable` repeats the rule that registers the Account route in Routes.js.
  const handleOkInvoiceMissingModal = () => {
    const target = getInvoiceMissingTarget({
      isFeaturesLoaded,
      isExpensesDisabled,
      isAdmin,
      isAccountAvailable: isAdmin ? isTreezorEnabled || isPsd2ObAvailable : isPsd2ObAvailable && hasObAccounts,
    });

    handleCancelInvoiceMissingModal();

    if (target === INVOICE_MISSING_TARGETS.EXPENSES) {
      // Not getSearchParamsFromLocation: with a hash present it reads the hash, not the search.
      const expenseId = new URLSearchParams(search).get(EXPENSES_PAGE_SEARCH_KEYS.SELECTED_EXPENSE_ID);
      navigate(getInvoiceMissingExpensesUrl(employeeEmail, expenseId));
      return;
    }

    if (target === INVOICE_MISSING_TARGETS.ACCOUNT) {
      // The email's expense id is for the Expenses page; the Account page does not read it.
      const accountSearch = new URLSearchParams(search);
      accountSearch.delete(EXPENSES_PAGE_SEARCH_KEYS.SELECTED_EXPENSE_ID);
      const filterParams = transactionsHelpers.getMissingInfoFilterParams(employeeId);
      const searchWithParams = locationHelpers.buildSearchWithParams(accountSearch.toString(), filterParams);
      navigate(`${routes.transactionsList}?${searchWithParams}`);
      return;
    }

    if (target === INVOICE_MISSING_TARGETS.NONE) return;

    navigate(routes.overview);
  }

  const handleCancelUnmatchedInvoicesModal = () => setUnmatchedInvoicesModalProps(DEFAULT_MODAL_PROPS);

  const handleTransactionsInvoiceMissing = () => {
    handleOpenModal(setTransactionsInvoiceMissingModalProps);
    updatePopupState([popupSettingsConstants.TRANSACTIONS_INVOICE_MISSING, popupSettingsConstants.UNMATCHED_INVOICES]);
  }

  const handleUnmatchedInvoices = () => {
    handleOpenModal(setUnmatchedInvoicesModalProps, `${routes.invoicesList}${search}#outstanding`);
    updatePopupState([popupSettingsConstants.UNMATCHED_INVOICES]);
  }

  const handleOpenBankAccountDetails = () => {
    dispatch(mainActions.displayWalletDetailsInitially());
    navigate('/');
  }

  const updatePopupState = (keys = []) => {
    dispatch(userActions.updateEmployeePopupSettings({
      data: {'popup_settings': keys},
      successCallback: dispatch(userActions.getEmployeePopupSettings())
    }));
  }

  const isOpen = (getter) => companyState && getter?.open;

  return (
    <>
      {children}

      <InvoiceMissingModal
        {...transactionsInvoiceMissingModalProps}
        onCancel={handleCancelInvoiceMissingModal}
        onOk={handleOkInvoiceMissingModal}
        open={isOpen(transactionsInvoiceMissingModalProps)}
      />

      <UnmatchedInvoicesModal
        {...unmatchedInvoicesModalProps}
        onCancel={handleCancelUnmatchedInvoicesModal}
        onOk={handleCancelUnmatchedInvoicesModal}
        open={isOpen(unmatchedInvoicesModalProps)}
      />
    </>
  );
};

export default HashWindowManager;
